import { modifier } from 'ember-modifier';

import { action } from '@ember/object';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';

// @ts-ignore
import type { Dropdown } from 'ember-basic-dropdown/components/basic-dropdown';
interface IArgs {}
interface INamed {
  focusApi?: Dropdown;
  focusId?: string;
}
const FOCUS_ATTRIBUTE = 'data-focus-id';
const CONTENT_ATTRIBUTE = 'data-focus-content-id';

export default class BvFocusZone extends Component<IArgs> {
  focusApis: Record<string, Dropdown> = {};
  currentFocusedElement: HTMLElement | null = null;

  focusUnit = modifier(
    (element: HTMLElement, _positional, named: INamed) => {
      const { focusApi } = named;

      const isTrigger = !!focusApi;

      if (isTrigger) {
        this.currentFocusedElement = element;
        this.focusApis[focusApi.uniqueId] = { ...focusApi, element };
        this.currentFocusedElement.addEventListener('focus', this.handleFocusTrigger);
      }

      next(this, () => {
        const { focusApi, focusId } = named;

        const isTrigger = !!focusApi;
        const isContent = element.contains(document.activeElement) && focusId;

        if (isTrigger) {
          this.currentFocusedElement = element;
          this.focusApis[focusApi.uniqueId] = { ...focusApi, element };
          this.currentFocusedElement.setAttribute(FOCUS_ATTRIBUTE, focusApi.uniqueId);
          this.currentFocusedElement.addEventListener('keydown', this.handleKeydownTrigger);
        } else if (isContent) {
          this.currentFocusedElement = document.activeElement! as HTMLElement;
          this.currentFocusedElement.setAttribute(CONTENT_ATTRIBUTE, focusId);
          this.currentFocusedElement.addEventListener('keydown', this.handleKeydownContent);
        }

        return () => {
          if (this.currentFocusedElement) {
            this.currentFocusedElement.removeAttribute(CONTENT_ATTRIBUTE);
            this.currentFocusedElement.removeEventListener('keydown', this.handleKeydownContent);
            this.currentFocusedElement.removeEventListener('focus', this.handleFocusTrigger);
            this.currentFocusedElement.removeEventListener('keydown', this.handleKeydownTrigger);
          }
          if (focusApi) {
            delete this.focusApis[focusApi.uniqueId];
          }
          if (this.isDestroying || this.isDestroyed) {
            return;
          }
        };
      });
    },
    {
      eager: false
    }
  );

  @action
  handleKeydownContent(event: KeyboardEvent) {
    const target = event.target as HTMLElement;

    if (!target) {
      return;
    }

    const id = target.getAttribute(CONTENT_ATTRIBUTE);

    this.handleKeydown(event, id);
  }

  @action
  handleKeydownTrigger(event: KeyboardEvent) {
    const target = event.target as HTMLElement;

    if (!target) {
      return;
    }

    const id = target.getAttribute(FOCUS_ATTRIBUTE);
    this.handleKeydown(event, id);
  }

  @action
  handleFocusTrigger(event: FocusEvent) {
    const target = event.target as HTMLElement;

    if (!target) {
      return;
    }

    const id = target.getAttribute(FOCUS_ATTRIBUTE);

    if (id && Object.values(this.focusApis).every((dropdown) => !dropdown.isOpen)) {
      this.focusApis[id].actions.open(event);
    }
  }

  @action
  handleKeydown(event: KeyboardEvent, id: string | null) {
    switch (event.code) {
      case 'Tab':
        if (id) {
          this.focusApis[id].actions.close(event, false);
        }

        const focusUnits = document.querySelectorAll(`[${FOCUS_ATTRIBUTE}]`);
        if (!focusUnits) {
          return;
        }
        let nextId;
        const isShifted = event.shiftKey;
        focusUnits.forEach((unit: HTMLElement, index, units) => {
          if (unit.getAttribute(FOCUS_ATTRIBUTE) === id) {
            const indexToSwitch = isShifted ? index - 1 : index + 1;
            nextId = units[indexToSwitch]?.getAttribute(FOCUS_ATTRIBUTE);
          }
        });

        if (nextId) {
          this.focusApis[nextId]?.actions.open(event);
        }

        break;
      default:
        break;
    }
  }
}
